import { useEffect, useState, useMemo, useRef } from 'react';
import { Box, Typography, List, ListItem, ListItemText, ListItemIcon, Button, Paper, TextField, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Divider, Tooltip } from '@mui/material';
import { CheckCircle, Cancel, Folder, Person, Delete } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { clearEnrollmentState, communicationGet, communicationSave, documentDestroy, documentGet, documentSave, fileSave, getEnrollmentData, sendNotification } from 'store/application/student-enrollment';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import * as snackbar from 'utils/snackbar';
import Heading from './Heading';
import EnrollmentDetails from './EnrollmentDetails';
import Documents from './Documents';
import CommunicationHistory from './CommunicationHistory';
import { hasFullPermission } from 'store';
import Confirm from 'components/Dialog';
import { save as sendTemplateNotification } from 'store/notification/notification';

const ENROLLMENT_TEMPLATE = {
  extend: 'enrollment-notification-extend',
  reminder: 'enrollment-notification-reminder',
};

export default function EnrollmentProgress({ student, school, onClose }) {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const dispatch = useDispatch();
  const [documents, setDocuments] = useState(null);
  const [loading, setLoading] = useState(false);
  const enrollment = useSelector(state => state.application.studentEnrollment.enrollment);
  const documentTypes = useSelector(state => state.lookup.types?.filter(t => t.category === 'enrollmentDocument') || []);
  const { t } = useTranslation();
  const isNurse = hasFullPermission('application.nurse');
  const isSpecial = hasFullPermission('application.special');
  const isAdmin = hasFullPermission('application.application');
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);

  const schoolEncodedId = useMemo(() => {
    if (!school?.id) return null;
    return btoa(encodeURIComponent(school.id));
  }, [school?.id]);
  const fetchVersionRef = useRef(0);

  const daysRemaining = useMemo(() => {
    if (!enrollment?.enrollmentPackageView?.expiresOn) return null;

    const today = dayjs().format('YYYY-MM-DD');
    const expirationDate = dayjs(enrollment?.enrollmentPackageView?.expiresOn)
    const remaining = expirationDate.diff(today, 'day');

    return remaining;
  }, [enrollment?.enrollmentPackageView?.expiresOn]);

  const isExpired = daysRemaining !== null && daysRemaining < 0;

  const isNotifiedToday = useMemo(() => {
    if (!enrollment?.lastNotificationDate) return false;
    return dayjs(enrollment.lastNotificationDate).isSame(dayjs(), 'day');
  }, [enrollment?.lastNotificationDate]);

  useEffect(() => {
    const fetchAll = async () => {
      if (!student?.id || !schoolEncodedId) return;

      const currentVersion = ++fetchVersionRef.current;

      setDocuments(null);
      setSelectedCategory(null);
      setMessageDialogOpen(false);
      setMessageText('');
      setSelectedDoc(null);
      setLoading(true);

      dispatch(clearEnrollmentState());

      try {
        const enrollmentResult = await dispatch(getEnrollmentData({ id: schoolEncodedId }));
        if (currentVersion !== fetchVersionRef.current) return;

        const enrollmentData = enrollmentResult?.payload;
        if (!enrollmentData?.id) {
          setLoading(false);
          return;
        }

        if (documentTypes.length === 0) {
          setLoading(false);
          return;
        }

        const enrollmentEncodedId = btoa(encodeURIComponent(enrollmentData.id));
        const result = await dispatch(documentGet({ id: enrollmentEncodedId }));
        if (currentVersion !== fetchVersionRef.current) return; // stale

        const response = result?.payload || result;

        let docsArray = [];
        if (response) {
          if (Array.isArray(response)) {
            docsArray = response;
          } else if (typeof response === 'object' && response.data) {
            docsArray = Array.isArray(response.data) ? response.data : [response.data];
          }
        }

        if (docsArray.length) {
          const mappedDocuments = await Promise.all(
            docsArray.map(async (doc, index) => {
              if (currentVersion !== fetchVersionRef.current) return null;
              let notes = [];
              let communications = [];
              try {
                const commRes = await dispatch(communicationGet({ id: doc.id }));
                const commData = commRes?.payload || commRes;
                if (Array.isArray(commData)) {
                  communications = commData.filter(
                    c => c.enrollmentPackageDocumentId === doc.id || c.enrollmentPackageDocumentId === doc.apiId
                  );
                  notes = communications.map(c => c.explanation).filter(Boolean);
                } else if (commData && commData.explanation) {
                  communications = [commData];
                  notes = [commData.explanation];
                }
              } catch (err) {
                console.log('Communication fetch error,', err.message);
              }

              const docType = documentTypes.find(dt => dt.code === doc.type);

              return {
                id: `panel${index + 1}`,
                category: doc.type || '',
                name: docType?.name || `Document ${index + 1}`,
                status: doc.status || 'pending',
                isUploading: false,
                files: doc.enrollmentPackageFiles || [],
                apiId: doc.id,
                notes,
                communications,
                sequence: docType?.seq ?? 9999,
                isRequired: docType?.value?.required || false
              };
            })
          );

          if (currentVersion !== fetchVersionRef.current) return;
          const sortedDocuments = mappedDocuments.filter(Boolean).sort((a, b) => a.sequence - b.sequence);
          setDocuments(sortedDocuments);
        } else {
          setDocuments([]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        if (currentVersion === fetchVersionRef.current) setLoading(false);
      }
    };

    fetchAll();
  }, [student?.id, schoolEncodedId, documentTypes.length, dispatch]);

  useEffect(() => {
    if (!documents || !selectedCategory) {
      setSelectedCategory('enrollmentDetails');
    }
  }, [documents, selectedCategory]);

  const categories = useMemo(() => {
    if (!documents) return [];
    const categoryMap = {};
    documents.forEach(doc => {
      if (!categoryMap[doc.category]) {
        categoryMap[doc.category] = {
          category: doc.category,
          name: doc.name,
          documents: [],
          sequence: doc.sequence,
        };
      }
      categoryMap[doc.category].documents.push(doc);
    });

    let allCategories = Object.values(categoryMap).sort((a, b) => a.sequence - b.sequence);

    if (isAdmin) {
      return allCategories;
    }

    if (isNurse && isSpecial) {
      allCategories = allCategories.filter(cat => cat.category === 'immunization' || cat.category === 'special');
    } else if (isNurse) {
      allCategories = allCategories.filter(cat => cat.category === 'immunization');
    } else if (isSpecial) {
      allCategories = allCategories.filter(cat => cat.category === 'special');
    }

    return allCategories;
  }, [documents]);

  const currentCategoryDocs = useMemo(() => {
    const category = categories.find(c => c.category === selectedCategory);
    return category?.documents || [];
  }, [categories, selectedCategory]);

  const currentCategoryInfo = useMemo(() => {
    const category = categories.find(c => c.category === selectedCategory);
    return category || {};
  }, [categories, selectedCategory]);

  const currentCategoryCommunications = useMemo(() => {
    if (!currentCategoryDocs.length) return [];

    const allComms = [];
    currentCategoryDocs.forEach(doc => {
      if (Array.isArray(doc.communications)) {
        doc.communications.forEach(comm => {
          allComms.push({
            ...comm,
            documentName: doc.name
          });
        });
      }
    });

    return allComms.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [currentCategoryDocs]);

  const getCategoryStatus = (docs) => {
    if (docs.every(d => d.status === 'approved')) return 'approved';
    if (docs.some(d => d.status === 'rejected')) return 'rejected';
    if (docs.some(d => d.files.length > 0)) return 'uploaded';
    return 'pending';
  };

  const handleApprove = (doc) => {
    dispatch(documentSave({
      id: doc.apiId,
      status: 'approved'
    })).then(() => {
      setDocuments(prevDocs =>
        prevDocs.map(d =>
          d.apiId === doc.apiId ? { ...d, status: 'approved' } : d
        )
      );
      snackbar.success(t('Document approved successfully'));
    });
  };

  const handleOpenRejectDialog = (doc) => {
    setSelectedDoc(doc);
    setMessageText('');
    setMessageDialogOpen(true);
  };

  const handleUndoClick = async (doc) => {
    try {
      await dispatch(documentSave({
        id: doc.apiId,
        status: 'pending'
      })).then(() => {
        setDocuments(prevDocs =>
          prevDocs.map(d =>
            d.apiId === doc.apiId
              ? { ...d, status: 'pending' }
              : d
          )
        );
        snackbar.success(t('Document status reset successfully'));
      });
    } catch (error) {
      console.error('Error opening message dialog:', error);
      snackbar.error(t('Error Undoing Status'));
    }
  };

  const handleDialogConfirm = () => {
    try {
      dispatch(communicationSave({
        enrollmentPackageDocumentId: selectedDoc.apiId,
        explanation: messageText,
      })).then((result) => {
        const savedComm = result?.payload || result;
        dispatch(documentSave({
          id: selectedDoc.apiId,
          status: 'rejected'
        })).then(() => {
          setDocuments(prevDocs =>
            prevDocs.map(d =>
              d.apiId === selectedDoc.apiId
                ? {
                  ...d,
                  status: 'rejected',
                  communications: [savedComm, ...(d.communications || [])],
                  notes: [savedComm.explanation, ...(d.notes || [])]
                }
                : d
            )
          );
          snackbar.success(t('Document rejected successfully'));
          notifyDocumentRejected(selectedDoc, messageText);
        });
        setMessageDialogOpen(false);
        setMessageText('');
        setSelectedDoc(null);
      });
    } catch (error) {
      console.error('Error rejecting document:', error);
      snackbar.error(t('Error rejecting document'));
    }
  };

  const handleFileDelete = (file) => {
    dispatch(fileSave({
      id: file.id,
      deleted: true
    })).then(() => {
      setDocuments(prevDocs =>
        prevDocs.map(d => ({
          ...d,
          files: d.files.filter(f => f.id !== file.id)
        }))
      );
    });
  };


  const notifyDocumentRejected = async (doc) => {
    const applicant = enrollment?.applicationSchool?.applicationStudent;

    if (!applicant?.parentEmail) {
      snackbar.warning(t('No parent email on file — no notification was sent.'));
      return;
    }

    const result = await dispatch(sendTemplateNotification({
      templateCode: 'enrollment-document-rejected',
      payload: [{
        _id: doc.apiId,
      }],
    }));

    if (result?.error) {
      snackbar.error(result.error?.data?.message || t('Document rejected, but the parent could not be notified.'));
      return;
    }

    snackbar.success(t('Parent notified that the document needs re-uploading.'));
  };

  const handleDocumentDelete = (doc) => {
    dispatch(documentDestroy({
      id: doc.apiId,
    })).then(() => {
      setDocuments(prevDocs =>
        prevDocs.filter(d => d.apiId !== doc.apiId)
      );
      setSelectedCategory('enrollmentDetails');
      snackbar.success(t('Document deleted successfully'));
    }).catch((error) => {
      console.error('Error deleting document:', error);
      snackbar.error(t('Error deleting document'));
    });
  }

  const notifyEnrollment = async (templateCode, failureMessage) => {
    const applicationSchoolId = enrollment?.applicationSchoolId ?? enrollment?.applicationSchool?.id;

    if (!applicationSchoolId) {
      snackbar.warning(t('No application on file — no notification was sent.'));
      return false;
    }

    const result = await dispatch(sendTemplateNotification({
      templateCode,
      payload: [{
        _id: applicationSchoolId,
      }],
    }));

    if (result?.error) {
      snackbar.error(result.error?.data?.message || failureMessage);
      return false;
    }

    return true;
  };

  const handleNotifyParent = async () => {
    // createAsyncThunk resolves either way and reports failure on the action, so the save has to be
    // checked here — a catch block around the dispatch would never run
    const response = await dispatch(sendNotification({
      id: enrollment.id,
      lastNotificationDate: dayjs()
    }));

    if (response?.error) {
      snackbar.error(response.error?.data?.message || t('Error Sending Notification'));
      return;
    }

    const notified = await notifyEnrollment(
      ENROLLMENT_TEMPLATE.reminder,
      t('Reminder saved, but the parent could not be notified.'),
    );

    if (notified) snackbar.success(t('Notification sent successfully'));
  };

  const handleExtendEnrollment = async () => {
    const response = await dispatch(sendNotification({
      id: enrollment.id,
      extendDate: dayjs()
    }));

    if (response?.error) {
      snackbar.error(response.error?.data?.message || t('Error Extending Enrollment Period'));
      return;
    }

    const notified = await notifyEnrollment(
      ENROLLMENT_TEMPLATE.extend,
      t('Enrollment period extended, but the parent could not be notified.'),
    );

    if (notified) snackbar.success(t('Enrollment period extended successfully'));
  };

  const handleDeleteClick = (doc) => {
    setDocToDelete(doc);
    setOpenDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (docToDelete) {
      handleDocumentDelete(docToDelete);
      setOpenDeleteConfirm(false);
      setDocToDelete(null);
    }
  };

  const handleCloseConfirm = () => {
    setOpenDeleteConfirm(false);
    setDocToDelete(null);
  };

  return (
    <Box>
      <Heading student={student} school={school} onClose={onClose} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}>
          <CircularProgress sx={{ mb: 2 }} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', height: 'calc(100vh - 100px)', bgcolor: 'background.default' }}>
          <Paper sx={{ width: { xs: 150, md: 300 }, borderRadius: 0, borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
            <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
              {isAdmin && (
                <ListItem
                  button
                  selected={selectedCategory === 'enrollmentDetails'}
                  onClick={() => setSelectedCategory('enrollmentDetails')}
                  sx={{
                    borderLeft: '4px solid',
                    borderLeftColor: selectedCategory === 'enrollmentDetails' ? 'primary.main' : 'transparent',
                    bgcolor: selectedCategory === 'enrollmentDetails' ? 'action.selected' : 'transparent'
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Person color="action" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("Enrollment Details")}
                    primaryTypographyProps={{ fontWeight: selectedCategory === 'enrollmentDetails' ? 600 : 400 }}
                  />
                </ListItem>
              )}

              <Divider sx={{ mt: 1 }} />
              <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                  {t("Document Categories")}
                </Typography>
              </Box>

              {categories.map((cat) => {
                const status = getCategoryStatus(cat.documents);

                return (
                  <ListItem
                    key={cat.category}
                    button
                    selected={cat.category === selectedCategory}
                    onClick={() => setSelectedCategory(cat.category)}
                    sx={{
                      borderLeft: '4px solid',
                      borderLeftColor: cat.category === selectedCategory ? 'primary.main' : 'transparent',
                      bgcolor: cat.category === selectedCategory ? 'action.selected' : 'transparent'
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {status === 'approved' ? <CheckCircle color="success" fontSize="small" /> :
                        status === 'rejected' ? <Cancel color="error" fontSize="small" /> :
                          <Folder color={cat?.documents[0]?.files?.length > 0 ? 'primary' : 'action'} fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={cat.name}
                      secondary={`${cat.documents.reduce((acc, d) => acc + d.files.length, 0)} ${t('file(s)')}`}
                      primaryTypographyProps={{ fontWeight: cat?.category === selectedCategory ? 600 : 400 }}
                    />
                    {!cat.documents[0]?.isRequired && (
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Tooltip 
                          title={cat.documents[0]?.files?.length > 0 ? t('Cannot delete document with uploaded files') : ''}
                          placement="top"
                          arrow
                        >
                          <span>
                            <Delete
                              color={cat.documents[0]?.files?.length > 0 ? 'disabled' : 'inherit'}
                              fontSize="small"
                              onClick={(e) => {
                                if (cat.documents[0]?.files?.length > 0) {
                                  e.stopPropagation();
                                  return;
                                }
                                handleDeleteClick(cat.documents[0]);
                              }}
                            />
                          </span>
                        </Tooltip>
                      </ListItemIcon>
                    )}
                  </ListItem>
                );
              })}
            </List>
          </Paper>

          <Box sx={{ flex: 1, p: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
            {selectedCategory === 'enrollmentDetails' && isAdmin ? (
              <EnrollmentDetails
                enrollment={enrollment}
                categories={categories}
                daysRemaining={daysRemaining}
                isExpired={isExpired}
                isNotifiedToday={isNotifiedToday}
                onNotifyParent={handleNotifyParent}
                onExtendEnrollment={handleExtendEnrollment}
              />
            ) : (
              <>
                <Documents
                  currentCategoryInfo={currentCategoryInfo}
                  currentCategoryDocs={currentCategoryDocs}
                  onApprove={handleApprove}
                  onReject={handleOpenRejectDialog}
                  onUndo={handleUndoClick}
                  onDeleteFile={handleFileDelete}
                />
                <CommunicationHistory communications={currentCategoryCommunications} />
              </>
            )}
          </Box>
        </Box>
      )}

      <Dialog open={messageDialogOpen} onClose={() => setMessageDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t("Reject Document")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("Please provide a reason for rejecting this document:")}
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            placeholder={t("Rejection reason...")}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessageDialogOpen(false)}>{t("Cancel")}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDialogConfirm}
            disabled={!messageText.trim()}
          >
            {t("Confirm Reject")}
          </Button>
        </DialogActions>
      </Dialog>
      <Confirm
        dialogTitle={t('Confirm Document Deletion')}
        message={t('Are you sure you want to delete this document? This action cannot be undone.')}
        onConfirm={handleConfirmDelete}
        open={openDeleteConfirm}
        onClose={handleCloseConfirm}
        requireDeleteTyping={true}
      />
    </Box>
  );
}